import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Avatar from '../components/Avatar';
import { useAuth } from '../AuthContext';
import ConversationList from './ConversationList';
import { CreateGroupDialog } from '../dm/GroupDialogs';
import { Motion, presets } from '../motion';

const OWN_STATUS = { online: 'online', away: 'away', dnd: 'dnd', invisible: undefined };
const STATUS_LABEL = { online: 'Online', away: 'Away', dnd: 'Do not disturb', invisible: 'Invisible' };

// Search for users to open or start a conversation
function ConversationSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const inputRef = useRef();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // /dm?search=1 (old /dm/new and /search links) focuses the field
  useEffect(() => {
    if (new URLSearchParams(location.search).get('search')) inputRef.current?.focus();
  }, [location.search]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`/api/users/search?q=${encodeURIComponent(query.trim())}&full=true`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setResults(res.data.filter(u => u.id !== user?.id));
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, user?.id]);

  const pick = (u) => {
    setQuery('');
    setResults([]);
    navigate(`/dm/user/${u.id}`);
  };

  return (
    <div className="relative" data-noswipe>
      <input
        ref={inputRef}
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') setQuery(''); if (e.key === 'Enter' && results[0]) pick(results[0]); }}
        placeholder="Find or start a conversation"
        className="w-full rounded-sm border-0 bg-black px-3 py-2 font-body text-text outline-none placeholder:text-text-dim focus:ring-2 focus:ring-purple-dim"
      />
      {query.trim() && (
        <Motion {...presets.dropdown('top center')} className="absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-y-auto rounded-lg border border-solid border-border bg-surface p-1 shadow-modal">
          {searching && results.length === 0 && <div className="px-3 py-3 text-sm text-text-dim">Searching…</div>}
          {!searching && results.length === 0 && <div className="px-3 py-3 text-sm text-text-dim">No users found</div>}
          {results.map(u => (
            <button
              key={u.id}
              type="button"
              onClick={() => pick(u)}
              className="flex w-full cursor-pointer items-center gap-3 rounded-sm border-0 bg-transparent px-2 py-2 text-left hover:bg-surface-2"
            >
              <Avatar src={u.avatar} size={30} />
              <span className="min-w-0">
                <span className="block truncate font-display text-sm font-bold text-text">{u.display_name || u.username}</span>
                <span className="block truncate text-xs text-text-dim">@{u.username}</span>
              </span>
            </button>
          ))}
        </Motion>
      )}
    </div>
  );
}

// Own avatar, name and status at the bottom, like Discord
export function UserPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  if (!user) return null;
  const pref = user.status_preference || 'online';
  return (
    <div className="flex items-center gap-2 bg-surface-2 px-2 pb-[calc(8px_+_var(--safe-bottom))] pt-2">
      <button
        type="button"
        onClick={() => navigate(`/profile/${user.username}`)}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-sm border-0 bg-transparent px-1 py-1 text-left hover:bg-surface-2"
      >
        <Avatar src={user.avatar} size={32} status={OWN_STATUS[pref]} />
        <span className="min-w-0">
          <span className="block truncate font-display text-sm font-extrabold text-text">{user.display_name || user.username}</span>
          <span className="block truncate text-xs text-text-dim">{STATUS_LABEL[pref]}</span>
        </span>
      </button>
      <button
        type="button"
        title="Settings"
        aria-label="Settings"
        onClick={() => navigate('/settings')}
        className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent text-text-muted hover:bg-surface-2 hover:text-text"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>
    </div>
  );
}

// "Direct Messages" heading with the button for a new group
function ListHeader() {
  const navigate = useNavigate();
  const { nicknames } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center px-4 pb-1 pt-4">
      <span className="flex-1 text-[11px] font-extrabold uppercase tracking-wider text-text-dim">Direct Messages</span>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="New group"
        aria-label="New group"
        className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent text-text-dim hover:bg-surface-2 hover:text-text"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
      </button>
      {open && (
        <CreateGroupDialog
          nicknames={nicknames}
          onClose={() => setOpen(false)}
          onCreated={(id) => { setOpen(false); navigate(`/dm/${id}`); }}
        />
      )}
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside className="flex h-full min-w-0 flex-1 flex-col bg-surface md:w-sidebar md:flex-none">
      <div className="border-0 border-b border-solid border-black px-2.5 py-2.5">
        <ConversationSearch />
      </div>
      <ListHeader />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <ConversationList />
      </div>
      <UserPanel />
    </aside>
  );
}
